import React, { useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout } from '@/components/layout';
import { LabelInput } from '@/components/ui/inputs';
import type { MonthlyCutManagementItem } from '@/api/domains/attendance';
import { useMonthlyCutDetail } from '@/hooks/attendance/monthlyCuts/useMonthlyCutDetail';


// Marcador de campo sin valor. Coincide con el guion que usa el listado para `process`.
const EMPTY_VALUE = '-';

export interface MonthlyCutDetailPanelProps {
  cut: MonthlyCutManagementItem;
}

/**
 * Panel lateral de solo lectura con el detalle de un corte mensual (ASIS-4964).
 *
 * Los datos ya formateados (empresa, año, mes, rango, proceso y estado) se toman de la fila del
 * listado; el alcance y las agrupaciones asignadas se cargan aparte porque el listado no los
 * expone. No ofrece ninguna acción de escritura: el panel es el destino de la opción "Ver detalle",
 * disponible con permiso de solo lectura.
 */
export const MonthlyCutDetailPanel: React.FC<MonthlyCutDetailPanelProps> = ({ cut }) => {
  const { t: translate } = useTranslation();
  const { detail, assignedGroups, groupsLoaded } = useMonthlyCutDetail({ monthlyCutId: cut.id });

  const scopeValue = useMemo(() => {
    if (!detail) return '';
    return detail.applyToAllCompanies
      ? translate('attendance:monthlyCutDetail.fields.scopeAllCompanies')
      : translate('attendance:monthlyCutDetail.fields.scopeSingleCompany');
  }, [detail, translate]);

  // Gatea por la carga de las agrupaciones, no por `detail`: viajan en peticiones independientes
  // (Promise.all), así que un detalle fallido no debe vaciar agrupaciones que sí llegaron bien.
  // Mientras no terminen de cargar, el campo queda vacío en vez de anunciar "sin agrupaciones", que
  // sería un dato falso.
  const groupsValue = useMemo(() => {
    if (!groupsLoaded) return '';
    return assignedGroups.length > 0
      ? assignedGroups.map((group) => group.name).join(', ')
      : translate('attendance:monthlyCutDetail.fields.groupsEmpty');
  }, [groupsLoaded, assignedGroups, translate]);

  return (
    <SidePanelFormLayout data-testid="monthly-cut-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.id')}
          value={String(cut.id)}
          disabled
          readOnly
          data-testid="monthly-cut-detail-id"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.company')}
          value={cut.companyName}
          disabled
          readOnly
          data-testid="monthly-cut-detail-company"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.year')}
          value={String(cut.year)}
          disabled
          readOnly
          data-testid="monthly-cut-detail-year"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.month')}
          value={cut.month}
          disabled
          readOnly
          data-testid="monthly-cut-detail-month"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.range')}
          value={cut.range}
          disabled
          readOnly
          data-testid="monthly-cut-detail-range"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.process')}
          value={cut.process || EMPTY_VALUE}
          disabled
          readOnly
          data-testid="monthly-cut-detail-process"
        />
        <LabelInput
          label={translate('attendance:monthlyCutsList.fields.state')}
          value={cut.state}
          disabled
          readOnly
          data-testid="monthly-cut-detail-state"
        />
        {/*
          Alcance y agrupaciones no vienen en la fila del listado: se cargan aparte y llegan vacíos
          en el primer render. El spinner lo pinta el contenedor del panel mientras eso ocurre.
        */}
        <LabelInput
          label={translate('attendance:monthlyCutDetail.fields.scope')}
          value={scopeValue}
          disabled
          readOnly
          data-testid="monthly-cut-detail-scope"
        />
        <LabelInput
          label={translate('attendance:monthlyCutDetail.fields.groups')}
          value={groupsValue}
          disabled
          readOnly
          data-testid="monthly-cut-detail-groups"
        />
      </SidePanelFormLayout.Fields>
    </SidePanelFormLayout>
  );
};

export default MonthlyCutDetailPanel;
